import {StyledAccessesCounterCard, StyledAccessesCounterCardItem} from './style'
import {Text} from '@/components/ui/text/Text'
import {Trans, useTranslation} from '@/translations/i18n'
import {Divider} from '@/components/ui/divider/Divider'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {UserCheck01Icon, UserX01Icon} from '@/components/ui/icon/Icon'
import {useTheme} from 'styled-components'
import {ProgressBar} from '@/components/ui/progress-bar/ProgressBar'
import {useGetAccessesDetail} from '@/features/accesses/queries/useGetAccessesDetail'
import {ResponseType} from '@/types'
import {useOutletContext} from 'react-router-dom'
import {ProjectExtended} from '../../types'
import {useGetPlannedAccessesDetails} from '@/features/planned-accesses/queries/useGetPlannedAccessesDetails'
import {usePlannedAccessesCalculations} from '../../hooks/usePlannedAccessesCalculations'
import {useAccessesCalculations} from '../../hooks/useAccessesCalculations'
import {useMemo} from 'react'
import {AccessesCounterCardEmptyState} from './AccessesCounterCardEmptyState'
import {getProgressBarVariant} from '../../utils'

export const AccessesCounterCard = () => {
    const {t} = useTranslation()
    const {project} = useOutletContext<{project: ProjectExtended}>()
    const {palette} = useTheme()
    const {
        data: accesses,
        isLoading: isLoadingAccesses,
        isError: isErrorAccesses
    } = useGetAccessesDetail({
        params: {
            projectId: project.id,
            responseType: ResponseType.enum.extended
        }
    })
    const {data: plannedAccesses, isLoading: isLoadingPlannedAccesses} = useGetPlannedAccessesDetails({
        urlParams: {
            projectId: project.id
        }
    })

    const {totalPlannedAccessesInAbsence, totalPlannedAccessesInPresence} = usePlannedAccessesCalculations(
        project.durationDays ?? 0,
        plannedAccesses
    )

    const {totalAccessesInPresence, totalAccessInAbsence} = useAccessesCalculations(accesses)

    const inPresencePercentage = useMemo(() => {
        return (totalAccessesInPresence / (totalPlannedAccessesInPresence || 0)) * 100
    }, [totalAccessesInPresence, totalPlannedAccessesInPresence, isErrorAccesses])

    const inAbsencePercentage = useMemo(() => {
        return (totalAccessInAbsence / (totalPlannedAccessesInAbsence || 0)) * 100
    }, [totalAccessInAbsence, totalPlannedAccessesInAbsence, isErrorAccesses])

    const inAbsenceprogressBarVariant = useMemo(() => {
        return getProgressBarVariant(inAbsencePercentage)
    }, [inAbsencePercentage])

    const inPresenceprogressBarVariant = useMemo(() => {
        return getProgressBarVariant(inPresencePercentage)
    }, [inPresencePercentage])

    const isEmpty = useMemo(() => {
        return (
            accesses?.in_person_accesses.length === 0 &&
            accesses?.non_in_person_accesses.length === 0 &&
            accesses?.treatments.length === 0
        )
    }, [accesses])

    if (isEmpty || isLoadingAccesses || isLoadingPlannedAccesses)
        return <AccessesCounterCardEmptyState isLoading={isLoadingAccesses || isLoadingPlannedAccesses} />

    return (
        <StyledAccessesCounterCard>
            <StyledAccessesCounterCardItem direction="column" gap={1} fullWidth>
                <Flexbox gap={1} align="center">
                    <UserCheck01Icon size={18} fill={palette.primary[400]} />
                    <Text size="textXs">{t('projects:performed_in_presence_count')}</Text>
                </Flexbox>
                <Flexbox fullWidth justify="end">
                    <Text>
                        <Trans
                            i18nKey="projects:count_of_total"
                            components={{b: <Text render="span" weight={500} />}}
                            values={{count: totalAccessesInPresence, total: totalPlannedAccessesInPresence || 0}}
                        />
                    </Text>
                </Flexbox>
                <ProgressBar rounded percentage={inPresencePercentage} variant={inPresenceprogressBarVariant} />
            </StyledAccessesCounterCardItem>
            <Divider direction="vertical" />
            <StyledAccessesCounterCardItem direction="column" gap={1} fullWidth>
                <Flexbox gap={1} align="center">
                    <UserX01Icon size={18} fill={palette.primary[400]} />
                    <Text size="textXs">{t('projects:performed_in_absence_count')}</Text>
                </Flexbox>
                <Flexbox fullWidth justify="end">
                    <Text>
                        <Trans
                            i18nKey="projects:count_of_total"
                            components={{b: <Text render="span" weight={500} />}}
                            values={{count: totalAccessInAbsence, total: totalPlannedAccessesInAbsence || 0}}
                        />
                    </Text>
                </Flexbox>
                <ProgressBar rounded percentage={inAbsencePercentage} variant={inAbsenceprogressBarVariant} />
            </StyledAccessesCounterCardItem>
        </StyledAccessesCounterCard>
    )
}
